/**
 * @file    useEnginePersistence.ts
 * @scope   ENTERPRISE
 * @summary Serializes the active engine subtype's selection back into the widget before saving.
 * @consumes Active engine slice, EnterpriseContext, widgetSerializer, engine serializer core
 * @produces `applyEnterpriseDataToWidget`
 * @sideEffects Mutates the widget passed in (same behaviour as the original inline code).
 * @notes Only invoked for Enterprise V2 charts; legacy charts must never call it.
 */
import { Widget } from 'models/WidgetModel';
import { extractEnterpriseDataToWidget } from 'components/DiyWidgetEngine/Subtypes/Enterprise/utils/storage/widgetSerializer';
import { useEnterpriseContext } from 'components/DiyWidgetEngine/Subtypes/Enterprise/State/EnterpriseContext';
import { writeEnvelope } from 'components/DiyWidgetEngine/engine/serialization/envelope';
import { serializeWithDelta } from 'components/DiyWidgetEngine/engine/serialization/baseSerializer';
import { useFeatureFlagResolver } from 'components/DiyWidgetEngine/engine/featureFlags/useFeatureFlagResolver';
import { ENTERPRISE_DESCRIPTOR } from 'components/DiyWidgetEngine/Subtypes/Enterprise/descriptor';
import type { AnyEngineDescriptor } from 'components/DiyWidgetEngine/ChartTypes/engineChartTypeRegistry';
import { ActiveEngineDescriptor } from './useActiveEngineDescriptor';
import { useActiveEngineSlice } from './useActiveEngineSlice';

export const ENVELOPE_WRITES_FLAG = 'useDiyEngineEnvelopeWrites';

// ⚡ ENTERPRISE V2 — NEW WORLD. Do not use for legacy charts.

export interface EnginePersistence {
  applyEnterpriseDataToWidget: (widget: Widget, trend: any, availableCollections: any) => void;
}

/**
 * @param descriptor Active subtype. Enterprise (or none) keeps the historic serializer; any other
 *                   subtype goes through the engine core + its own delta.
 */
export function useEnginePersistence(descriptor?: ActiveEngineDescriptor): EnginePersistence {
  const { state } = useEnterpriseContext();
  const { slice } = useActiveEngineSlice(descriptor);
  const isFlagEnabled = useFeatureFlagResolver();
  const active: AnyEngineDescriptor = descriptor ?? ENTERPRISE_DESCRIPTOR;
  const isEnterprise = active.id === ENTERPRISE_DESCRIPTOR.id;

  const applyEnterpriseDataToWidget = (
    widget: Widget,
    trend: any,
    availableCollections: any
  ): void => {
    if (isEnterprise || !active.serializerDelta) {
      widget.customWidgetConfig = extractEnterpriseDataToWidget(
        widget.customWidgetConfig,
        state,
        trend,
        availableCollections
      );
    } else {
      // WHY no `trend` option: non-Enterprise subtypes (CPR) keep the trend in their own slice.
      widget.customWidgetConfig = serializeWithDelta(
        widget.customWidgetConfig,
        slice,
        active.serializerDelta,
        { availableCollections }
      );
    }
    // Phase 7: additive envelope, gated until backend confirms (see writeEnvelope TODO).
    if (isFlagEnabled(ENVELOPE_WRITES_FLAG)) {
      writeEnvelope(widget.customWidgetConfig, active.id, active.serializerDelta?.version ?? 1);
    }
    // WHY: Enterprise grids always expose the CSV export action; the original code hard-coded it
    // here rather than in the options builder, and that ordering is preserved on purpose.
    widget.options = { ...widget.options, actions: ['EXPORT_TO_CSV'] };
  };

  return { applyEnterpriseDataToWidget };
}

export default useEnginePersistence;
